import { useNavigate } from "react-router-dom";
import { Building2, Calendar, ChevronDown, ChevronUp, FileText } from "lucide-react";
import { EstadoBadge } from "./estado-badge";
import type { Licitacion } from "@/types/licitacion";

interface LicitacionTableProps {
  licitaciones: Licitacion[];
  onSort?: (field: string) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

function formatCurrency(value: number | null): string {
  if (value === null) return "\u2014";
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "\u2014";
  return new Date(dateStr).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

function SortIcon({ field, sortBy, sortOrder }: { field: string; sortBy?: string; sortOrder?: string }) {
  if (sortBy !== field) return null;
  return sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
}

export function LicitacionTable({ licitaciones, onSort, sortBy, sortOrder }: LicitacionTableProps) {
  const navigate = useNavigate();
  const thClass = "text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700";

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className={thClass} onClick={() => onSort?.("titulo")}>
              <span className="flex items-center gap-1">T\u00edtulo <SortIcon field="titulo" sortBy={sortBy} sortOrder={sortOrder} /></span>
            </th>
            <th className={`${thClass} hidden md:table-cell`} onClick={() => onSort?.("organismo")}>
              <span className="flex items-center gap-1">Organismo <SortIcon field="organismo" sortBy={sortBy} sortOrder={sortOrder} /></span>
            </th>
            <th className={thClass}>Estado</th>
            <th className={`${thClass} hidden lg:table-cell`} onClick={() => onSort?.("presupuesto_base")}>
              <span className="flex items-center gap-1">Presupuesto <SortIcon field="presupuesto_base" sortBy={sortBy} sortOrder={sortOrder} /></span>
            </th>
            <th className={thClass} onClick={() => onSort?.("fecha_limite")}>
              <span className="flex items-center gap-1">Fecha L\u00edmite <SortIcon field="fecha_limite" sortBy={sortBy} sortOrder={sortOrder} /></span>
            </th>
            <th className={`${thClass} hidden lg:table-cell`}>Docs</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {licitaciones.map((lic) => (
            <tr key={lic.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => navigate(`/licitaciones/${lic.id}`)}>
              <td className="px-4 py-3">
                <p className="text-sm font-medium text-gray-900 line-clamp-1">{lic.titulo}</p>
                {lic.referencia && <p className="text-xs text-gray-400 mt-0.5">{lic.referencia}</p>}
              </td>
              <td className="px-4 py-3 hidden md:table-cell">
                <div className="flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-sm text-gray-600 line-clamp-1">{lic.organismo}</span>
                </div>
              </td>
              <td className="px-4 py-3"><EstadoBadge estado={lic.estado} /></td>
              <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell">{formatCurrency(lic.presupuesto_base)}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-sm text-gray-600">{formatDate(lic.fecha_limite)}</span>
                </div>
              </td>
              <td className="px-4 py-3 hidden lg:table-cell">
                <div className="flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-sm text-gray-600">{lic.document_count}</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
